/**
 * External store for live custom-decal preview during slider/pad drags.
 * Keyed by decal ID. Bypasses Redux so dragging stays smooth (no per-tick
 * dispatch / clamp / scene re-render) and undo history is not polluted — the
 * decal plane reads its previewed transform straight from this store and the
 * committed value is dispatched to Redux only on release. Mirrors
 * `previewDecalFeatures` (camera lens / cutout / home indicator).
 */

export interface PreviewCustomDecal {
  offsetU: number
  offsetV: number
  size: number
  rotation: number
}

let snapshot: Record<string, PreviewCustomDecal> | null = null
const listeners = new Set<() => void>()

const notify = () => listeners.forEach((l) => l())

/**
 * Set (merge) preview for a specific decal ID.
 * @param id — decal ID
 * @param base — full fallback (from Redux) used when no preview record exists yet
 * @param changes — partial overrides to merge on top
 */
export const setPreviewCustomDecal = (id: string, base: PreviewCustomDecal, changes: Partial<PreviewCustomDecal>) => {
  const existing = snapshot?.[id]
  snapshot = {
    ...snapshot,
    [id]: {
      ...base,
      ...existing,
      ...changes,
    },
  }
  notify()
}

export const clearPreviewCustomDecal = () => {
  snapshot = null
  notify()
}

export const getPreviewCustomDecalSnapshot = (): Record<string, PreviewCustomDecal> | null => snapshot

export const subscribePreviewCustomDecal = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
